import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pill, Trash, Check } from "@phosphor-icons/react";

export default function Medications() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", dosage: "", frequency: "", times: "", prescribed_by: "", notes: "" });

  const load = async () => { const r = await api.get("/health/medications"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, times: form.times ? form.times.split(",").map((t) => t.trim()).filter(Boolean) : null };
    try {
      await api.post("/health/medications", payload);
      toast.success("Medication added.");
      setForm({ name: "", dosage: "", frequency: "", times: "", prescribed_by: "", notes: "" });
      setAdding(false);
      load();
    } catch { toast.error("Could not save."); }
  };

  const del = async (id) => { await api.delete(`/health/medications/${id}`); load(); };

  const logDose = async (id) => {
    try {
      await api.post("/health/medications/dose", { medication_id: id, taken_at: new Date().toISOString() });
      toast.success("Dose logged.");
    } catch { toast.error("Could not log."); }
  };

  return (
    <div className="space-y-8" data-testid="medications-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-saffron mb-2">Medicine</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl">Medications</h1>
          <p className="text-ink-700 mt-2">Reminders and history — for prescriptions, supplements, and vitamins.</p>
        </div>
        <button onClick={() => setAdding(!adding)} className="btn-primary" data-testid="add-med-btn"><Plus weight="thin" size={18} /> Add medication</button>
      </header>

      {adding && (
        <form onSubmit={submit} className="vansha-card p-6 space-y-5" data-testid="med-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className="overline mb-2 block">Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line" placeholder="Folic Acid" data-testid="med-name" /></div>
            <div><label className="overline mb-2 block">Dosage</label><input value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} className="input-line" placeholder="5 mg" data-testid="med-dosage" /></div>
            <div><label className="overline mb-2 block">Frequency</label><input value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="input-line" placeholder="Once daily" data-testid="med-frequency" /></div>
            <div><label className="overline mb-2 block">Times (comma-separated)</label><input value={form.times} onChange={(e) => setForm({ ...form, times: e.target.value })} className="input-line" placeholder="08:00, 20:00" data-testid="med-times" /></div>
            <div><label className="overline mb-2 block">Prescribed by</label><input value={form.prescribed_by} onChange={(e) => setForm({ ...form, prescribed_by: e.target.value })} className="input-line" data-testid="med-doctor" /></div>
          </div>
          <div><label className="overline mb-2 block">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-line" rows="2" data-testid="med-notes" /></div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="med-submit">Save</button>
          </div>
        </form>
      )}

      {items.length === 0 ? <p className="text-ink-500" data-testid="med-empty">No medications yet.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((m) => (
            <div key={m.id} className="vansha-card p-6" data-testid={`med-${m.id}`}>
              <Pill weight="thin" size={22} className="text-terracotta mb-2" />
              <h3 className="font-serif text-xl">{m.name}</h3>
              {m.dosage && <p className="text-sm text-ink-700 mt-1">{m.dosage} · {m.frequency}</p>}
              {m.times?.length > 0 && <p className="text-xs text-ink-500 mt-1">Times: {m.times.join(", ")}</p>}
              {m.prescribed_by && <p className="text-xs text-ink-500 mt-1">Prescribed by {m.prescribed_by}</p>}
              {m.notes && <p className="text-sm text-ink-700 mt-2 italic">{m.notes}</p>}
              <div className="flex gap-2 mt-4">
                <button onClick={() => logDose(m.id)} className="btn-secondary text-sm flex-1" data-testid={`log-dose-${m.id}`}><Check weight="thin" size={14} /> Log dose</button>
                <button onClick={() => del(m.id)} className="btn-ghost p-2" data-testid={`del-med-${m.id}`}><Trash weight="thin" size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
